import { MicVAD } from "@ricky0123/vad-web";
import { useCallback, useEffect, useRef, useState } from "react";

const NO_SPEECH_TIMEOUT_MS = 15_000;

const VAD_OPTIONS = {
  baseAssetPath: "https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.30/dist/",
  onnxWASMBasePath: "https://cdn.jsdelivr.net/npm/onnxruntime-web@1.24.2/dist/",
  model: "v5" as const,
  positiveSpeechThreshold: 0.5,
  negativeSpeechThreshold: 0.35,
};

export function useNoSpeechDetection(isRecording: boolean, isPaused: boolean, mediaRecorder: MediaRecorder | null) {
  const [noSpeechDetected, setNoSpeechDetected] = useState(false);
  const vadRef = useRef<Awaited<ReturnType<typeof MicVAD.new>> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitializingRef = useRef(false);
  const hasAlertedRef = useRef(false);

  const clearSilenceTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    if (hasAlertedRef.current) return;
    timerRef.current = setTimeout(() => {
      setNoSpeechDetected(true);
      hasAlertedRef.current = true;
    }, NO_SPEECH_TIMEOUT_MS);
  }, [clearSilenceTimer]);

  const dismissAlert = useCallback(() => {
    setNoSpeechDetected(false);
  }, []);

  // Initialize VAD when recording starts with a stream
  useEffect(() => {
    const stream = mediaRecorder?.stream;

    if (!isRecording || isPaused || !stream?.active || isInitializingRef.current) {
      return;
    }

    // Already initialized
    if (vadRef.current) {
      vadRef.current.start();
      startSilenceTimer();
      return;
    }

    let cancelled = false;
    isInitializingRef.current = true;

    MicVAD.new({
      ...VAD_OPTIONS,
      getStream: async () => stream,
      onSpeechStart: () => {
        clearSilenceTimer();
      },
      onSpeechEnd: () => {
        startSilenceTimer();
      },
      onVADMisfire: () => {
        // Brief noise, not real speech — keep the timer running
      },
    })
      .then((vad) => {
        if (cancelled) {
          vad.pause();
          return;
        }
        vadRef.current = vad;
        vad.start();
        startSilenceTimer();
      })
      .catch((err) => {
        console.error("[VAD] Failed to initialize:", err);
      })
      .finally(() => {
        isInitializingRef.current = false;
      });

    return () => {
      cancelled = true;
    };
  }, [isRecording, isPaused, mediaRecorder, startSilenceTimer, clearSilenceTimer]);

  // Pause VAD when recording pauses
  useEffect(() => {
    if (isPaused && vadRef.current) {
      vadRef.current.pause();
      clearSilenceTimer();
    }
  }, [isPaused, clearSilenceTimer]);

  // Stop VAD when recording stops entirely
  useEffect(() => {
    if (!isRecording && !isPaused && vadRef.current) {
      vadRef.current.pause();
      clearSilenceTimer();
      vadRef.current = null;
      hasAlertedRef.current = false;
      setNoSpeechDetected(false);
    }
  }, [isRecording, isPaused, clearSilenceTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (vadRef.current) {
        vadRef.current.pause();
        vadRef.current = null;
      }
      clearSilenceTimer();
    };
  }, [clearSilenceTimer]);

  return { noSpeechDetected, dismissAlert };
}
